import React, { useCallback, useRef } from "react";
import {
    ColumnChooser,
    ColumnChooserSearch,
    ColumnFixing,
    DataGrid,
    Export,
    FilterPanel,
    FilterRow,
    GroupPanel,
    Grouping,
    Item,
    Pager,
    Paging,
    Scrolling,
    Sorting,
    StateStoring,
    Toolbar,
} from "devextreme-react/data-grid";
import { GridLayout } from "../../../common/GridLayout";
import GridLayoutManager from "../../../gridLayoutCustomization/GridLayoutManager";
import { ALLOWED_PAGE_SIZES } from "../constants";
import { renderColumns } from "./GridColumns";

/**
 * One read-only grid, for every tab in the bottom pane.
 *
 * There are thirteen grid-shaped bottom tabs and they differ only in three things: the store, the
 * grid-layout key, and the columns. Writing thirteen DataGrids with the same twelve feature elements
 * would be twelve copies of the same decisions, and the next tab would be a fourteenth
 * (Architecture.md R1). Columns arrive as data instead - see `bottom/Columns.js`, which holds each
 * tab's set with the captions taken from the Delphi DisplayLabels, and
 * `common/DispatchObjectColumns.js` for the four tabs that bind DispatchObjectBoardDTO.
 *
 * Every grid here is remote-operations: the stores are CustomStores over DataSourceLoader, so paging,
 * sorting, filtering and grouping resolve in SQL. No grid on this board edits anything, so none of the
 * DevExtreme CustomStore rules (Architecture.md 4-6, 12, 14-16) apply.
 *
 * @param {Array}  columns  column definitions - see renderColumns in ./GridColumns, which the top
 *                          pane's grid shares.
 */
const BottomGrid = (props) => {
    const {
        dataSource,
        keyExpr,
        gridKey,
        columns,
        accessToken,
        currentLayout,
        setCurrentLayout,
        defaultPageSize = 10,
    } = props;

    const dataGrid = useRef(null);

    const saveState = useCallback(
        (state) => GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout),
        [gridKey, accessToken, currentLayout]
    );

    const loadState = useCallback(
        () => GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout),
        [gridKey, accessToken, currentLayout]
    );

    return (
        <DataGrid
            ref={dataGrid}
            dataSource={dataSource}
            keyExpr={keyExpr}
            remoteOperations={true}
            allowColumnReordering={true}
            allowColumnResizing={true}
            columnAutoWidth={true}
            showBorders={true}
            hoverStateEnabled={true}
            height="100%"
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
        >
            {/* showScrollbar="always". DevExtreme resolves scrolling.useNative to false on this
                platform, so the rows view scrolls with a simulated scrollbar - `overflow: hidden`
                on .dx-scrollable-container and an 8px overlay thumb the component draws itself -
                and its desktop default is "onHover", which marks that thumb dx-state-invisible
                until the pointer is inside the grid. Measured on this screen: the bottom rows view
                offered 128px for 338px of rows with no scrollbar drawn, so the only cue that the
                rest existed was the pager count. "always" keeps the thumb on screen. */}
            <Scrolling mode="standard" showScrollbar="always" />
            <Sorting mode="multiple" />
            {/* Filter row and filter panel only. DEV-6402 also lists a header filter, but it is
                deliberately off on this screen: on a remote-operations grid each header filter
                popup issues its own DISTINCT query against the column, and these grids sit over
                the largest table in the product behind a fifteen-table join. The filter row
                covers the same need without that cost. */}
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <ColumnFixing enabled={true} />
            <Export enabled={true} allowExportSelectedData={true} />
            <Paging defaultPageSize={defaultPageSize} />
            <Pager
                visible={true}
                showPageSizeSelector={true}
                allowedPageSizes={ALLOWED_PAGE_SIZES}
                showInfo={true}
                showNavigationButtons={true}
            />
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
            </ColumnChooser>
            <StateStoring
                enabled={true}
                type="custom"
                storageKey={gridKey}
                customLoad={loadState}
                customSave={saveState}
            />

            {renderColumns(columns)}

            <Toolbar>
                <Item location="after">
                    <GridLayoutManager
                        gridKey={gridKey}
                        dataGrid={dataGrid}
                        setCurrentLayout={setCurrentLayout}
                    />
                </Item>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

export default BottomGrid;
